import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { Col, Form, Row } from "react-bootstrap";

import { useNavigate } from "react-router-dom";
import {
  Box,
  ButtonStyled,
  FormButtonStyled,
  HeadingStyled,
  LabelStyled,
  TextStyled,
  ThemeLoader,
} from "../../../../../components/styled/Elements";
import ValidatedInput from "../../../../../components/common/inputFields/ValidatedInput";
import {
  LeftArrowBlack,
  RightArrowWhite,
  RightArrowWithoutBar,
} from "../../../../../components/styled/AllImages";
import { toast } from "react-toastify";
import BillingAPI from "../../../../../services/api/billingApi";

const LicenseStep2 = ({
  handleSteps,
  bankInfo,
  planInfo,
  addLicenseFrm,
  register,
  control,
  getFieldState,
  errors,
  watch,
  loading,
  totalAmount,
  user,
  validateCurrentStep,
  action,
  onHide,
}) => {
  const MOYASAR_PUBLIC_KEY = process.env.REACT_APP_MOYASAR_PUBLIC_KEY;
  const REACT_APP_API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [moyasarLoading, setMoyasarLoading] = useState(true);
  const [moyasarForm, setMoyasarForm] = useState(null);
  const moyasarFormRef = useRef(null);
  const formRef = useRef(null);

  const useSavedCard = watch("use_saved_card") || 0;

  const finalSignup = async (formObj) => {
    const formData = new FormData();
    Object.entries(formObj).forEach(([key, value]) => {
      formData.append(key, value);
    });

    try {
      const { code, message, data } = await BillingAPI.upgradeOrgPlan(formData);
      if (code === 200) {
        window.open(data?.payment_url, "_self");
        onHide();
      }
      if (code === 100 || code === 204) {
        toast.error(message);
      }
    } catch (error) {
    } finally {
      setSubmitLoading(false);
    }
  };

  const signUpStepTwo = async (token = "") => {
    setSubmitLoading(true);
    const baseCost = parseFloat(planInfo?.amount);
    const baseObj = {
      plan_id: planInfo?.id,
      plan_duration: planInfo?.duration || 1,
      total_amount: totalAmount,
      // payment_method: paymentMethod,
      use_saved_card: useSavedCard,
      // number_of_licenses: numOfLicenses,
      action: action,
      sub_total: baseCost,
      ...(bankInfo?.vat && {
        vat_percentage: bankInfo?.vat?.vat || 0,
        vat_value: (parseFloat(bankInfo?.vat?.vat) / 100) * baseCost || 0,
      }),
    };
    const paymentSpecifics = { token_id: token };
    finalSignup({
      ...baseObj,
      ...paymentSpecifics,
    });
  };

  const handleSubmit = async () => {

    const internalSubmitBtn = formRef.current?.querySelector('button[type="submit"]');

    if (internalSubmitBtn) {
      setSubmitLoading(true);
      internalSubmitBtn.click();
    } else {
      toast.error("The payment form is not ready yet. Please wait.");
      console.error("Internal Submit button not found inside formRef");
      setSubmitLoading(false);
    }
  };

  useEffect(() => {
    const initMoyasar = () => {
      if (!window.Moyasar || !formRef.current) return;

      try {
        formRef.current.innerHTML = "";
        const form = window.Moyasar.init({
          element: formRef.current,
          amount: Math.round(totalAmount * 100), // Convert SAR to halalas
          currency: "SAR",
          description: `Plan Subscription - ${planInfo?.title || "Plan"} (User: ${user?.id})`,
          publishable_api_key: MOYASAR_PUBLIC_KEY,
          // Moyasar redirects here natively after card auth processing
          callback_url: `${REACT_APP_API_BASE_URL}/billing/moyasar-callback-upgrade-plan`,
          methods: ["creditcard"],
          supported_networks: ["mada", "visa", "mastercard", "amex"],
          metadata: {
            plan_id: planInfo?.id,
            user_id: user?.id,
            // "number_of_licenses": "",
            // "number_of_quotations": "",
            // "number_of_invoices": "",
            plan_duration: planInfo?.duration,
            sub_total: parseFloat(planInfo?.amount),
            ...(bankInfo?.vat && {
              vat_percentage: bankInfo?.vat?.vat || 0,
              vat_value:
                (parseFloat(bankInfo?.vat?.vat) / 100) *
                  parseFloat(planInfo?.amount) || 0,
            }),
            use_saved_card: useSavedCard,
          },
          on_completed: async function (payment) {
            try {
              signUpStepTwo(payment?.id);
            } catch (error) {
              console.error("Card signup validation sync error:", error);
              toast.error(
                "Could not sync transaction details. Please try again.",
              );
              throw error;
            }
          },
        });

        moyasarFormRef.current = form;
      } catch (error) {
        console.error("Failed to initialize Moyasar:", error);
        toast.error("Failed to load payment form. Please try again.");
      }
    };

    const script = document.querySelector('script[src*="moyasar"]');

    if (window.Moyasar) {
      initMoyasar();
    } else if (script) {
      const handleScriptLoad = () => initMoyasar();
      script.addEventListener("load", handleScriptLoad);
      return () => script.removeEventListener("load", handleScriptLoad);
    } else {
      console.error("Moyasar script tag missing from index.html");
      toast.error("Payment integration script missing.");
    }

    return () => {
      if (moyasarForm && typeof moyasarForm.unmount === "function") {
        moyasarForm.unmount();
      }
    };
  }, [planInfo?.amount, user?.id, formRef]);

  return (
    <Step2Wrapper>
      <Box>
        <>
          {submitLoading ? (
            <ThemeLoader />
          ) : (
            <Box className="mb-4">
              <HeadingStyled size="24px" weight="bold" color="var(--text)">
                {t("pay_through_card")}
              </HeadingStyled>
              <TextStyled size="16px" color="var(--text2)">
                {t("please_enter_card_info_bellow")}
              </TextStyled>
            </Box>
          )}
          <Box ref={formRef} className="mb-4 mysr-form"></Box>
          {/* <Box id="checkout-container"></Box> */}
          {/* <Box>
            <ValidatedInput
              name={"card_holder_name"}
              label={"card_holder_name"}
              placeholder={"chNamePlaceholder"}
              type="input"
              fieldType="text"
              className={"mb-3 combined flex-wrap p-0"}
              bg="var(--white)"
              register={register}
            />
            <ValidatedInput
              name={"card_number"}
              label={"card_number"}
              placeholder={"card_number"}
              type="input"
              fieldType="text"
              className={"mb-3 combined flex-wrap p-0"}
              bg="var(--white)"
              register={register}
            />
            <Row>
              <Col lg={8}>
                <ValidatedInput
                  name={"expiry_date"}
                  label={"expiry_date"}
                  placeholder={"expiry_date"}
                  type="input"
                  fieldType="date"
                  className={"mb-3 combined flex-wrap p-0"}
                  bg="var(--white)"
                  register={register}
                />
              </Col>
              <Col lg={4}>
                <ValidatedInput
                  name={"cvv"}
                  label={"cvv"}
                  placeholder={"cvv"}
                  type="input"
                  fieldType="number"
                  className={"mb-3 combined flex-wrap p-0"}
                  bg="var(--white)"
                  register={register}
                  classNameLabel="w-auto"
                />
              </Col>
            </Row>
          </Box> */}
        </>
      </Box>
      {/* <FormButtonStyled
        label={t("make_payment")}
        color="var(--white)"
        bg="var(--themeColor)"
        className="w-100"
        clName="w-100 my-4"
        type="button"
      /> */}
      <Box className="d-flex justify-content-end align-items-center gap-3 my-3">
        <ButtonStyled
          border="var(--borderExtraLight)"
          iconBefore={LeftArrowBlack}
          bg="transparent"
          label={t("Cancel")}
          color="var(--text)"
          type="button"
        />
        <ButtonStyled
          bg="var(--themeColor)"
          icon={RightArrowWhite}
          label={addLicenseFrm ? t("submit") : t("sign_up")}
          color="var(--white)"
          // className="w-100"
          // clName="w-100 my-4"
          isLoading={submitLoading}
          disabled={submitLoading}
          onClick={() => handleSubmit()}
          type="submit"
        />
      </Box>
      {/* <FormButtonStyled
        label={addLicenseFrm ? t("submit") : t("sign_up")}
        color="var(--white)"
        bg="var(--themeColor)"
        className="w-100"
        clName="w-100 my-4"
        isLoading={submitLoading}
        disabled={submitLoading}
        onClick={() =>
          paymentMethod === "card" ? handleSubmit() : signUpStepTwo()
        }
        type="button"
      /> */}
    </Step2Wrapper>
  );
};

export default LicenseStep2;

const Step2Wrapper = styled.div`
  #card-footer {
    display: none;
  }
  #mysr button[type="submit"] {
    display: none !important;
  }
`;
